import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export type UserRole = 'FAN' | 'CLUB_EDITOR' | 'CLUB_SHOP_MANAGER' | 'CLUB_SUPPORT' | 'CLUB_ADMIN';

export interface UserListItemDto {
  id: string;
  email: string;
  displayName: string | null;
  nickname: string | null;
  role: UserRole;
  totalPoints: number;
  isActive: boolean;
  isBanned: boolean;
  trustScore: number;
  trustScoreUpdatedAt: string | null;
  createdAt: string;
  /** T-252: punti nella FASE selezionata (presente solo quando la lista è filtrata per periodo). */
  phasePoints?: number;
}

/** T-252 — campo di ordinamento server. 'points' = colonna "Punti" (totali o di fase). */
export type UserSortField = 'createdAt' | 'points' | 'trustScore';

export function useRecalcTrustScore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiClient.post<{ score: number }>(
        `/admin/trust-score/recalc/${userId}`,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useRecalcAllTrustScores() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<{ updated: number; tenantSlug: string }>(
        `/admin/trust-score/recalc-all`,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export interface PaginatedUsers {
  data: UserListItemDto[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface ListUsersFilter {
  page?: number;
  limit?: number;
  search?: string;
  role?: UserRole;
  /** T-252: ordinamento lato server. */
  sortBy?: UserSortField;
  order?: 'asc' | 'desc';
  /** T-252: se valorizzato, "Punti" e ordinamento usano i punti di questa fase. */
  periodId?: string;
}

const QK = {
  list: (f: ListUsersFilter) => ['users', f] as const,
  one: (id: string) => ['users', id] as const,
};

export function useUsers(filter: ListUsersFilter) {
  return useQuery({
    queryKey: QK.list(filter),
    queryFn: async () => {
      const params: Record<string, string | number> = {
        page: filter.page ?? 1,
        limit: filter.limit ?? 20,
      };
      if (filter.search) params.search = filter.search;
      if (filter.role) params.role = filter.role;
      if (filter.sortBy) params.sortBy = filter.sortBy;
      if (filter.order) params.order = filter.order;
      if (filter.periodId) params.periodId = filter.periodId;
      const res = await apiClient.get<PaginatedUsers>('/users', { params });
      return res.data;
    },
  });
}

export function useBanUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const res = await apiClient.post(`/users/${id}/ban`, { reason });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/**
 * T-154 — elimina un tifoso. Il server anonimizza se ha storico (riga tenuta,
 * dati personali rimossi, accesso disabilitato, ordini/punti conservati) o
 * cancella del tutto se è isolato. Irreversibile.
 */
export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete<{ id: string; deleted: true; mode: 'anonymized' | 'hard' }>(
        `/users/${id}`,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export interface AdminUpdateUserInput {
  firstName?: string;
  lastName?: string;
  email?: string;
  locale?: 'it' | 'en';
  role?: UserRole;
}

export function useAdminUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: AdminUpdateUserInput }) => {
      const res = await apiClient.patch(`/users/${id}/admin`, input);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useAdjustPoints() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, delta, reason }: { id: string; delta: number; reason: string }) => {
      const res = await apiClient.post<{ totalPoints: number; delta: number }>(
        `/users/${id}/points-adjust`,
        { delta, reason },
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
